import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DENOMINATIONS = [
  { value: 500, type: "note", bg: "#6b705c", ink: "#ffffff", name: "Five Hundred" },
  { value: 200, type: "note", bg: "#e07b2a", ink: "#3a1d00", name: "Two Hundred" },
  { value: 100, type: "note", bg: "#9d8ec4", ink: "#241a3a", name: "One Hundred" },
  { value: 50, type: "note", bg: "#3fa9c9", ink: "#04303d", name: "Fifty" },
  { value: 20, type: "note", bg: "#c8cf4a", ink: "#33390a", name: "Twenty" },
  { value: 10, type: "note", bg: "#8c5a3b", ink: "#ffffff", name: "Ten" },
  { value: 5, type: "coin", bg: "#c9a227", ink: "#3a2c00", name: "Five" },
  { value: 2, type: "coin", bg: "#b9c2c9", ink: "#22303a", name: "Two" },
  { value: 1, type: "coin", bg: "#cdd6dd", ink: "#22303a", name: "One" },
];

export const roundMoney = (n) => Math.round(Number(n) || 0);

export const formatINR = (n) =>
  `₹${roundMoney(n).toLocaleString("en-IN")}`;

/** Greedy breakdown of an amount into INR notes & coins. */
export function breakdownChange(amount) {
  let left = roundMoney(amount);
  const out = [];
  for (const d of DENOMINATIONS) {
    const count = Math.floor(left / d.value);
    if (count > 0) {
      out.push({ ...d, count });
      left -= count * d.value;
    }
  }
  return out;
}

/**
 * Recommended (fewest pieces) breakdown plus alternative single-denomination
 * combinations that add up to exactly the same amount.
 */
export function changeOptions(amount) {
  const amt = roundMoney(amount);
  if (amt <= 0) return [];
  const options = [
    { id: "best", label: "Fewest notes", notes: breakdownChange(amt) },
  ];
  for (const d of DENOMINATIONS) {
    if (options.length >= 5) break;
    if (amt % d.value !== 0) continue;
    const count = amt / d.value;
    if (count < 2 || count > 25) continue;
    options.push({
      id: `all-${d.value}`,
      label: `All ₹${d.value}`,
      notes: [{ ...d, count }],
    });
  }
  return options;
}

export const itemAmount = (it) => (it.sign || 1) * roundMoney(it.price) * (it.qty || 1);

export const itemsTotal = (items) =>
  items.reduce((s, it) => s + itemAmount(it), 0);

function authHeaders(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

export const posApi = {
  voidTransaction: (token, id) =>
    axios
      .post(`${API}/pos/transactions/${id}/void`, {}, authHeaders(token))
      .then((r) => r.data),
  createTransaction: (token, body) =>
    axios.post(`${API}/pos/transactions`, body, authHeaders(token)).then((r) => r.data),
  listTransactions: (token, params) =>
    axios
      .get(`${API}/pos/transactions`, { ...authHeaders(token), params })
      .then((r) => r.data),
  todaySummary: (token) =>
    axios.get(`${API}/pos/summary/today`, authHeaders(token)).then((r) => r.data),
  groups: (token, period) =>
    axios
      .get(`${API}/pos/summary/groups`, { ...authHeaders(token), params: { period } })
      .then((r) => r.data),
};

export function receiptLines(txn) {
  const width = 32;
  const line = (l, r) => {
    const pad = Math.max(width - l.length - r.length, 1);
    return l + " ".repeat(pad) + r;
  };
  const rows = [];
  rows.push({ text: "SKY BASKET", align: "center", bold: true, size: 2 });
  rows.push({ text: "Stationery & Gifts", align: "center" });
  rows.push({ text: "New Chandigarh, Punjab", align: "center" });
  rows.push({ text: "Ph: 098052 68169", align: "center" });
  rows.push({ text: "-".repeat(width) });
  rows.push({ text: line(txn.receipt_no || "", txn.local_time || "") });
  rows.push({ text: "-".repeat(width) });
  (txn.items || []).forEach((it, i) => {
    const label = it.label || `Item ${i + 1}`;
    const qty = it.qty > 1 ? ` x${it.qty}` : "";
    rows.push({
      text: line(`${label}${qty}`.slice(0, 20), `${itemAmount(it)}`),
    });
  });
  rows.push({ text: "-".repeat(width) });
  rows.push({ text: line("TOTAL", `Rs ${roundMoney(txn.total)}`), bold: true, size: 2 });
  rows.push({ text: line("Cash", `Rs ${roundMoney(txn.cash_received)}`) });
  rows.push({ text: line("Change", `Rs ${roundMoney(txn.change_given)}`) });
  rows.push({ text: "-".repeat(width) });
  rows.push({ text: "Thank you! Visit again", align: "center" });
  return rows;
}

export function receiptText(txn) {
  return receiptLines(txn)
    .map((r) => (r.align === "center" ? centerPad(r.text) : r.text))
    .join("\n");
}

function centerPad(text) {
  const width = 32;
  const pad = Math.max(Math.floor((width - text.length) / 2), 0);
  return " ".repeat(pad) + text;
}
